import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, isValid, parseISO, subDays } from 'date-fns';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaRedo, FaSave } from 'react-icons/fa';
import apiClient from '../services/apiClient';
import { isVisibleRoomCategory } from '../utils/roomFilters';

const formatDate = (date) => format(date, 'yyyy-MM-dd');

const normalizeDate = (value) => {
  if (!value) return '';
  if (value instanceof Date) return formatDate(value);

  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

  const parsed = parseISO(text);
  return isValid(parsed) ? formatDate(parsed) : '';
};

const parseDateInput = (value) => {
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : new Date();
};

const cellKey = (item) => String(item.id || `${item.room_id}-${normalizeDate(item.date)}`);

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [draftValues, setDraftValues] = useState({});
  const [dirtyCells, setDirtyCells] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const calendarRef = useRef(null);

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(selectedDate, index)),
    [selectedDate]
  );

  const weekStart = weekDates[0];
  const weekEnd = weekDates[weekDates.length - 1];

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/inventory', {
        params: {
          startDate: formatDate(weekStart),
          endDate: formatDate(weekEnd),
        },
      });

      const rows = (response.data.items || response.data || []).filter((item) =>
        isVisibleRoomCategory(item.room)
      );
      setInventory(rows);
      setDraftValues(
        rows.reduce((acc, item) => {
          acc[cellKey(item)] = String(Number(item.available_rooms || 0));
          return acc;
        }, {})
      );
      setDirtyCells({});
    } catch (error) {
      console.error('Error fetching inventory:', error);
      showToast(error.response?.data?.message || 'Failed to load inventory', 'error');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const groupedRows = useMemo(() => {
    const groups = {};

    inventory.forEach((item) => {
      if (!groups[item.room_id]) {
        groups[item.room_id] = {
          roomId: item.room_id,
          roomName: item.room?.name || `Room ${item.room_id}`,
          maxPerson: item.room?.maxPerson,
          items: [],
        };
      }

      groups[item.room_id].items.push(item);
    });

    return Object.values(groups).sort((a, b) => Number(a.roomId) - Number(b.roomId));
  }, [inventory]);

  const totalsByDate = useMemo(() => {
    return weekDates.map((date) => {
      const dateKey = formatDate(date);
      return inventory.reduce((total, item) => {
        if (normalizeDate(item.date) !== dateKey) return total;
        const value = Number(draftValues[cellKey(item)]);
        return total + (Number.isInteger(value) && value >= 0 ? value : 0);
      }, 0);
    });
  }, [draftValues, inventory, weekDates]);

  const totalAvailable = totalsByDate.reduce((sum, value) => sum + value, 0);

  const soldOutCells = useMemo(() => {
    return inventory.reduce((count, item) => {
      const value = Number(draftValues[cellKey(item)]);
      return count + (value === 0 ? 1 : 0);
    }, 0);
  }, [draftValues, inventory]);

  const dirtyCount = Object.keys(dirtyCells).length;

  const handleDateSelect = (value) => {
    if (!value) return;
    setSelectedDate(parseDateInput(value));
    setCalendarOpen(false);
  };

  const handleCellChange = (item, value) => {
    if (!/^\d*$/.test(value)) return;

    const key = cellKey(item);
    setDraftValues((prev) => ({ ...prev, [key]: value }));
    setDirtyCells((prev) => {
      const next = { ...prev };
      if (value === String(Number(item.available_rooms || 0))) {
        delete next[key];
      } else {
        next[key] = item;
      }
      return next;
    });
  };

  const discardChanges = () => {
    setDraftValues(
      inventory.reduce((acc, item) => {
        acc[cellKey(item)] = String(Number(item.available_rooms || 0));
        return acc;
      }, {})
    );
    setDirtyCells({});
    showToast('Changes discarded');
  };

  const saveChanges = async () => {
    const changedItems = Object.values(dirtyCells);
    if (changedItems.length === 0) {
      showToast('No changes to save');
      return;
    }

    const weeklyUpdates = changedItems.map((item) => {
      const value = Number(draftValues[cellKey(item)]);
      return {
        id: item.id,
        room_id: item.room_id,
        date: normalizeDate(item.date),
        available_rooms: value,
      };
    });

    const invalid = weeklyUpdates.some(
      (item) => !Number.isInteger(item.available_rooms) || item.available_rooms < 0
    );

    if (invalid) {
      showToast('Availability must be a non-negative whole number.', 'error');
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('/inventory/bulk-update', { weeklyUpdates });
      showToast(`Saved ${weeklyUpdates.length} change${weeklyUpdates.length > 1 ? 's' : ''}`);
      await fetchInventory();
    } catch (error) {
      console.error('Error saving inventory:', error);
      showToast(error.response?.data?.message || 'Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getCellItem = (items, date) => {
    const dateKey = formatDate(date);
    return items.find((item) => normalizeDate(item.date) === dateKey);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 text-slate-950 md:p-6">
      {toast && (
        <div
          className={`fixed right-4 top-4 z-50 rounded-lg px-4 py-2 text-sm font-medium text-white shadow-lg ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">
            Bulk Editor
          </p>
          <h1 className="mt-1 font-primary text-3xl font-bold text-slate-950 md:text-4xl">
            Inventory Management
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Edit weekly room availability, then save all changes together.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setSelectedDate((date) => subDays(date, 7))}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-semibold shadow-sm hover:bg-slate-50"
          >
            <FaChevronLeft /> Previous
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate(new Date())}
            className="rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-semibold shadow-sm hover:bg-slate-50"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate((date) => addDays(date, 7))}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-semibold shadow-sm hover:bg-slate-50"
          >
            Next <FaChevronRight />
          </button>

          <div className="relative" ref={calendarRef}>
            <button
              type="button"
              onClick={() => setCalendarOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              <FaCalendarAlt /> {format(weekStart, 'dd MMM')} - {format(weekEnd, 'dd MMM')}
            </button>
            {calendarOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 rounded-md border border-slate-200 bg-white p-3 shadow-lg">
                <input
                  type="date"
                  className="rounded border border-slate-300 p-2 text-sm"
                  value={formatDate(selectedDate)}
                  onChange={(event) => handleDateSelect(event.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-600">Total Available This Week</p>
          <p className="mt-2 text-4xl font-bold text-slate-950">{totalAvailable}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-600">Sold-Out Cells</p>
          <p className="mt-2 text-4xl font-bold text-slate-950">{soldOutCells}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-600">Unsaved Changes</p>
          <p className="mt-2 text-4xl font-bold text-slate-950">{dirtyCount}</p>
        </div>
      </div>

      <div className="mb-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={discardChanges}
          disabled={dirtyCount === 0 || saving}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaRedo /> Discard
        </button>
        <button
          type="button"
          onClick={saveChanges}
          disabled={dirtyCount === 0 || saving}
          className="inline-flex items-center gap-2 rounded-md bg-green-500 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-sm">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="sticky left-0 z-10 w-64 bg-slate-800 p-4 text-left font-semibold">
                  Room
                </th>
                {weekDates.map((date, index) => (
                  <th key={formatDate(date)} className="min-w-[120px] p-4 text-center font-semibold">
                    <span className="block">{format(date, 'EEE')}</span>
                    <span className="mt-1 block text-xs font-normal text-white">
                      {format(date, 'dd MMM')}
                    </span>
                    <span className="mt-2 block text-xs text-blue-100">
                      Total {totalsByDate[index] || 0}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-slate-500">
                    Loading inventory...
                  </td>
                </tr>
              ) : groupedRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-slate-500">
                    No inventory records for this week.
                  </td>
                </tr>
              ) : (
                groupedRows.map((row) => (
                  <tr key={row.roomId} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="sticky left-0 z-10 border-r border-slate-200 bg-white p-4">
                      <p className="font-semibold text-slate-950">{row.roomName}</p>
                      <p className="mt-1 text-xs text-slate-600">
                        Room ID {row.roomId}
                        {row.maxPerson ? ` | Max ${row.maxPerson}` : ''}
                      </p>
                    </td>
                    {weekDates.map((date) => {
                      const item = getCellItem(row.items, date);
                      const dateKey = formatDate(date);

                      if (!item) {
                        return (
                          <td key={dateKey} className="p-4 text-center text-slate-300">
                            -
                          </td>
                        );
                      }

                      const key = cellKey(item);
                      const value = draftValues[key] ?? '';
                      const isDirty = Boolean(dirtyCells[key]);
                      const isSoldOut = value !== '' && Number(value) === 0;

                      return (
                        <td key={dateKey} className="p-4 text-center">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={value}
                            onChange={(event) => handleCellChange(item, event.target.value)}
                            className={`h-12 w-24 rounded-md border px-2 text-center text-base font-semibold outline-none transition ${
                              isDirty
                                ? 'border-amber-400 bg-amber-50 text-amber-900'
                                : isSoldOut
                                  ? 'border-red-200 bg-red-50 text-red-700'
                                  : 'border-slate-300 bg-white text-slate-950'
                            } focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
                            aria-label={`${row.roomName} availability for ${format(date, 'dd MMM')}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
